/**
 * frontend/src/components/ProfilesManagerModal.ts
 * =================================================
 * Modal liviano para gestionar perfiles guardados.
 *
 * Muestra la lista de perfiles con:
 *   - Nombre, Banco/ERP, fecha de creación
 *   - Botón editar (reabre el wizard con el mapeo cargado)
 *   - Botón eliminar (con confirmación inline)
 *
 * Editar requiere que haya un archivo cargado en la pantalla de carga: el
 * wizard verifica el mapeo contra datos reales antes de dejar guardar, y sin
 * archivo esa comprobación no existe. Se puede guardar un perfil roto sin
 * enterarse, y un mapeo mal configurado no falla — devuelve números creíbles.
 *
 * Callbacks:
 *   onClose()          → se llama al cerrar (el padre recarga la lista)
 *   onEditar(perfil)   → el padre abre el wizard en modo edición
 */

import {
  listarPerfiles, eliminarPerfil, exportarPerfiles, importarPerfiles, ApiError,
} from "../api";
import type { SobreExportacion } from "../api";
import type { ColumnProfileResponse, TipoFuente } from "../types";
import { icon } from "../utils/icons";
import { escapeHtml } from "../utils/texto";
import { prepararDialogo } from "../utils/ui";

interface ProfilesManagerOptions {
  tipoFuente: TipoFuente;
  onClose: () => void;
  /** Perfil a editar. El padre abre el wizard; este modal se cierra. */
  onEditar: (perfil: ColumnProfileResponse) => void;
  /** ¿Hay un archivo cargado? Sin él no se puede verificar, así que no se edita. */
  hayArchivo: boolean;
}

export class ProfilesManagerModal {
  private _overlay: HTMLElement;
  private _opts: ProfilesManagerOptions;
  private _perfiles: ColumnProfileResponse[] = [];

  constructor(opts: ProfilesManagerOptions) {
    this._opts = opts;
    this._overlay = this._crearOverlay();
    document.body.appendChild(this._overlay);
    this._cargar();
    prepararDialogo(this._overlay, () => this.destroy());
  }

  destroy(): void {
    this._overlay.remove();
    this._opts.onClose();
  }

  private _crearOverlay(): HTMLElement {
    const el = document.createElement("div");
    el.className = "wizard-overlay";
    el.addEventListener("click", (e) => { if (e.target === el) this.destroy(); });
    return el;
  }

  private async _cargar(): Promise<void> {
    this._overlay.innerHTML = `
      <div class="wizard-modal wizard-modal--sm profiles-manager" role="dialog" aria-modal="true" aria-labelledby="pm-title">
        <div class="wizard-modal__header">
          <h3 id="pm-title">Perfiles de lectura guardados</h3>
          <button class="wizard-modal__close" id="pm-close" type="button" aria-label="Cerrar perfiles">${icon("close")}</button>
        </div>
        <div class="wizard-modal__body">
          <div class="spinner-inline"></div>
        </div>
      </div>`;

    this._overlay.querySelector("#pm-close")!.addEventListener("click", () => this.destroy());

    try {
      this._perfiles = await listarPerfiles(this._opts.tipoFuente);
      this._render();
    } catch (err) {
      this._overlay.querySelector(".wizard-modal__body")!.innerHTML = `
        <p class="wiz-error__msg">
          ${escapeHtml(err instanceof ApiError ? err.message : "No se pudieron cargar los perfiles.")}
        </p>`;
    }
  }

  private _render(): void {
    const body = this._overlay.querySelector(".wizard-modal__body")!;

    const importar = (clase: string) => `
      <button class="btn ${clase}" id="pm-importar" type="button"
        title="Carga perfiles desde un archivo exportado. Los que ya existan con el mismo nombre se omiten.">${icon("upload")} Importar</button>
      <input type="file" id="pm-importar-input" accept="application/json,.json" hidden />`;
    const resultado = `<div id="pm-import-result" class="pm-import-result" hidden></div>`;

    // Sin perfiles, importar es la acción principal: va dentro del mensaje.
    if (this._perfiles.length === 0) {
      body.innerHTML = `
        <div class="pm-empty">
          <p>No hay perfiles guardados todavía.</p>
          <p class="pm-empty__hint">Importe un archivo exportado, o suba un archivo y haga clic en <strong>Nuevo</strong> para crear uno.</p>
          ${importar("btn--primary pm-empty__accion")}
        </div>
        ${resultado}`;
      this._conectarExportImport(body);
      return;
    }

    const acciones = `
      <div class="pm-acciones">
        <button class="btn btn--secondary btn--sm" id="pm-exportar" type="button"
          title="Descarga todos los perfiles de este tipo en un archivo JSON. Sirve como backup y para llevarlos a otra instalación.">${icon("download")} Exportar</button>
        ${importar("btn--secondary btn--sm")}
      </div>
      ${resultado}`;

    body.innerHTML = `
      ${this._opts.hayArchivo ? "" : `
        <p class="pm-nota">
          Para editar un perfil, suba primero el archivo al que corresponde:
          los cambios se verifican contra datos reales antes de guardarse.
        </p>`}
      ${acciones}
      <ul class="pm-list">
        ${this._perfiles.map(p => `
          <li class="pm-item" data-id="${escapeHtml(p.id)}">
            <div class="pm-item__info">
              <strong class="pm-item__nombre" title="${escapeHtml(p.nombre)}">${escapeHtml(p.nombre)}</strong>
              ${p.banco_o_erp && p.banco_o_erp.trim().toLocaleLowerCase() !== p.nombre.trim().toLocaleLowerCase() ? `<span class="pm-item__banco">${escapeHtml(p.banco_o_erp)}</span>` : ""}
              <span class="pm-item__fecha">Creado: ${new Date(p.creado_en).toLocaleDateString("es-AR")}</span>
            </div>
            <div class="pm-item__actions">
              <button class="btn btn--secondary btn--sm pm-edit" data-id="${escapeHtml(p.id)}"
                ${this._opts.hayArchivo ? "" : "disabled"}
                title="${this._opts.hayArchivo
                  ? "Editar este perfil"
                  : "Suba el archivo correspondiente para poder editar: el mapeo se verifica contra datos reales antes de guardar."}"
              >Editar</button>
              <button class="btn btn--danger-outline btn--sm pm-delete" data-id="${escapeHtml(p.id)}">Eliminar</button>
            </div>
          </li>`).join("")}
      </ul>`;

    body.querySelectorAll<HTMLButtonElement>(".pm-delete").forEach(btn => {
      btn.addEventListener("click", () => this._confirmarEliminar(btn.dataset.id!));
    });

    this._conectarExportImport(body);

    body.querySelectorAll<HTMLButtonElement>(".pm-edit").forEach(btn => {
      btn.addEventListener("click", () => {
        const perfil = this._perfiles.find(p => p.id === btn.dataset.id);
        if (!perfil) return;
        this.destroy();
        this._opts.onEditar(perfil);
      });
    });
  }

  /**
   * Exportar / importar perfiles.
   *
   * Los perfiles son configuración —nombres de columna y formatos—, no datos
   * del cliente: no llevan importes ni movimientos. Por eso el archivo puede
   * viajar en claro y sirve de backup y de traslado entre instalaciones, que
   * de otro modo obliga a rehacer el wizard cuenta por cuenta.
   */
  private _conectarExportImport(body: Element): void {
    body.querySelector("#pm-exportar")?.addEventListener("click", async () => {
      try {
        const sobre = await exportarPerfiles(this._opts.tipoFuente);
        this._descargar(
          `perfiles-${this._opts.tipoFuente}-${new Date().toISOString().slice(0, 10)}.json`,
          JSON.stringify(sobre, null, 2),
        );
      } catch (err) {
        this._mostrarResultado(
          err instanceof ApiError ? err.message : "No se pudieron exportar los perfiles.",
          true,
        );
      }
    });

    const input = body.querySelector<HTMLInputElement>("#pm-importar-input");
    body.querySelector("#pm-importar")?.addEventListener("click", () => input?.click());

    input?.addEventListener("change", async () => {
      const archivo = input.files?.[0];
      if (!archivo) return;
      input.value = "";   // permite reintentar con el mismo archivo

      let sobre: SobreExportacion;
      try {
        sobre = JSON.parse(await archivo.text());
      } catch {
        this._mostrarResultado("El archivo no es un JSON válido.", true);
        return;
      }

      try {
        const r = await importarPerfiles(sobre);
        const partes: string[] = [];
        if (r.importados.length) partes.push(`${r.importados.length} importado(s)`);
        if (r.omitidos.length)   partes.push(`${r.omitidos.length} ya existía(n)`);
        if (r.fallidos.length)   partes.push(`${r.fallidos.length} con error`);

        // Se recarga primero: la lista se vuelve a dibujar y borraría el aviso.
        this._perfiles = await listarPerfiles(this._opts.tipoFuente);
        this._render();
        this._mostrarResultado(
          partes.join(" · ") || "No había perfiles en el archivo.",
          r.fallidos.length > 0,
          r.fallidos.map(f => `${f.nombre}: ${f.motivo}`),
        );
      } catch (err) {
        this._mostrarResultado(
          err instanceof ApiError ? err.message : "No se pudieron importar los perfiles.",
          true,
        );
      }
    });
  }

  private _descargar(nombre: string, contenido: string): void {
    const url = URL.createObjectURL(new Blob([contenido], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    a.click();
    URL.revokeObjectURL(url);
  }

  private _mostrarResultado(mensaje: string, esError: boolean, detalle: string[] = []): void {
    const el = this._overlay.querySelector<HTMLElement>("#pm-import-result");
    if (!el) return;
    el.className = `pm-import-result ${esError ? "is-error" : "is-ok"}`;
    el.innerHTML = escapeHtml(mensaje) + (detalle.length
      ? `<ul>${detalle.map(d => `<li>${escapeHtml(d)}</li>`).join("")}</ul>`
      : "");
    el.hidden = false;
  }

  private _confirmarEliminar(id: string): void {
    const item = this._overlay.querySelector<HTMLElement>(`.pm-item[data-id="${id}"]`);
    if (!item) return;

    const nombre = item.querySelector(".pm-item__nombre")?.textContent ?? "este perfil";
    item.innerHTML = `
      <div class="pm-confirm">
        <p>¿Eliminar <strong>${escapeHtml(nombre)}</strong>?</p>
        <div>
          <button class="btn btn--ghost btn--sm" id="pm-cancel-del">Cancelar</button>
          <button class="btn btn--danger btn--sm" id="pm-confirm-del">Sí, eliminar</button>
        </div>
      </div>`;

    item.querySelector("#pm-cancel-del")!.addEventListener("click", () => this._render());
    item.querySelector("#pm-confirm-del")!.addEventListener("click", async () => {
      const btn = item.querySelector<HTMLButtonElement>("#pm-confirm-del")!;
      btn.disabled    = true;
      btn.textContent = "Eliminando…";

      try {
        await eliminarPerfil(id);
        this._perfiles = this._perfiles.filter(p => p.id !== id);
        this._render();
      } catch (err) {
        btn.disabled    = false;
        btn.textContent = "Error — reintentar";
      }
    });
  }
}
